import React, {
    DetailedHTMLProps,
    forwardRef,
    InputHTMLAttributes,
    useImperativeHandle,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import * as Popover from '@radix-ui/react-popover';
import { ChevronDownIcon } from 'assets/icons';
import classNames from 'classnames';

import { AutocompleteOption } from './AutocompleteOption';

import styles from './Autocomplete.module.scss';
import optionStyles from './AutocompleteOption/AutocompleteOption.module.scss';

export type TAutocompleteOption = {
    value: string;
    label: string;
};

export interface IAutocompleteProps
    extends Omit<DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>, 'ref'> {
    options: TAutocompleteOption[];
    selected?: string | null;

    error?: boolean;

    action?: {
        label: string;
        icon?: JSX.Element;
        closeOnClick?: boolean;
        onClick: (e: React.MouseEvent<HTMLElement>) => void;
    };

    onValueChange?: (value: string | null) => void;
    onInputChange?: (value: string) => void;
}

export const Autocomplete = forwardRef<HTMLInputElement, IAutocompleteProps>(
    (
        { options, selected: selectedControlled, error, onValueChange, action, onInputChange, className, ...props },
        ref,
    ) => {
        const [value, setValue] = useState<string>('');

        const [selected, setSelected] = useState<TAutocompleteOption | undefined>(undefined);

        const [open, setOpen] = useState(false);

        const inputRef = useRef<HTMLInputElement>(null);

        useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

        const filteredOptions = useMemo(() => {
            const filtered = options.filter((option) =>
                String(option.label).toLowerCase().includes(value.toLowerCase()),
            );
            return filtered.splice(0, 5);
        }, [value, selected, options]);

        const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const inputValue = e.target.value;

            setValue(inputValue);
            onInputChange?.(inputValue);
            if (inputValue.length === 0) {
                setSelected(undefined);
                onValueChange?.(null);
                return;
            }
        };

        const handleSelect = (option: TAutocompleteOption) => {
            onValueChange?.(option.value);
            setSelected(option);
            setValue(option.label);
        };

        useLayoutEffect(() => {
            if (selectedControlled === undefined) {
                return;
            }
            if (selectedControlled === null) {
                setSelected(undefined);
                setValue('');
            }
            const selectedOption = options.find(({ value }) => selectedControlled === value);
            if (!selectedOption) return;
            setSelected(selectedOption);
            setValue(selectedOption.label);
        }, [selectedControlled, options]);

        return (
            <Popover.Root
                open={open}
                onOpenChange={(state) => {
                    setOpen(state);
                }}
            >
                <div className={styles['input-wrapper']}>
                    <input
                        autoComplete="off"
                        value={value}
                        onChange={handleInputChange}
                        onFocus={() => {
                            setOpen(true);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && filteredOptions.length === 1) {
                                e.stopPropagation();
                                e.preventDefault();
                                handleSelect(filteredOptions[0]);
                                setOpen(false);
                            }
                        }}
                        ref={inputRef}
                        className={classNames(
                            styles.input,
                            {
                                [styles.error]: !!error,
                            },
                            className,
                        )}
                        {...props}
                    />
                    <Popover.Trigger asChild>
                        <button
                            disabled={props.disabled}
                            className={classNames(styles['input-icon'], [{ [styles['input-icon-up']]: open }])}
                        >
                            <ChevronDownIcon />
                        </button>
                    </Popover.Trigger>
                    <Popover.Anchor style={{ width: '100%' }} />
                    <Popover.Portal>
                        <Popover.Content
                            sideOffset={4}
                            style={{
                                minWidth: inputRef.current ? `${inputRef.current.offsetWidth}px` : 'unset',
                                zIndex: 1000,
                            }}
                            align="start"
                            onFocusOutside={(e) => {
                                e.preventDefault();
                            }}
                            onInteractOutside={() => {
                                if (selected) {
                                    setValue(selected.label);
                                }
                            }}
                            onOpenAutoFocus={(e) => {
                                e.preventDefault();
                            }}
                            onCloseAutoFocus={(e) => {
                                e.preventDefault();
                            }}
                            asChild
                        >
                            <ul className={styles.content}>
                                {filteredOptions.map((option) => (
                                    <AutocompleteOption
                                        option={option}
                                        selected={selected?.value === option.value}
                                        setOpen={setOpen}
                                        onSelect={handleSelect}
                                        key={option.value}
                                    />
                                ))}
                                {action && (
                                    <li
                                        onClick={(e) => {
                                            if (action.closeOnClick) {
                                                setOpen(false);
                                            }
                                            action.onClick(e);
                                        }}
                                        className={classNames(optionStyles.option, optionStyles.actionOption)}
                                    >
                                        {action.icon}
                                        {action.label}
                                    </li>
                                )}
                            </ul>
                        </Popover.Content>
                    </Popover.Portal>
                </div>
            </Popover.Root>
        );
    },
);
